import type { Edge, Node } from 'reactflow';
import { compatibleLayerTypeByLayerId } from '@constants/canvas/general';
import { NodeDTOTypes } from '@store/services/nodes/types';

export type CanvasSlice = {
  nodes: Node[];
  edges: Edge[];
};

type NodesIndex = {
  source: Node[];
  byId: Map<string, Node>;
  selectedNodeId: string | null;
};

type EdgesIndex = {
  source: Edge[];
  activeEdge: Edge | null;
};

let nodesIndex: NodesIndex = {
  source: [],
  byId: new Map(),
  selectedNodeId: null,
};

let edgesIndex: EdgesIndex = { source: [], activeEdge: null };

/**
 * Per-node selectors run once per subscribed component on every store
 * notification, so scanning the arrays inside them costs O(N) per node — O(N²)
 * across a canvas. These rebuild only when the store hands over a new array,
 * turning each selector run into a map lookup.
 *
 * Correct because the store replaces `nodes` and `edges` rather than mutating
 * them; an in-place mutation would not be seen here.
 */
const indexNodes = (nodes: Node[]) => {
  if (nodes !== nodesIndex.source) {
    const byId = new Map<string, Node>();
    let selectedNodeId: string | null = null;

    nodes.forEach((node) => {
      byId.set(node.id, node);
      if (node.selected && selectedNodeId === null) selectedNodeId = node.id;
    });

    nodesIndex = { source: nodes, byId, selectedNodeId };
  }

  return nodesIndex;
};

const indexEdges = (edges: Edge[]) => {
  if (edges !== edgesIndex.source) {
    edgesIndex = {
      source: edges,
      activeEdge: edges.find((edge) => edge.selected) ?? null,
    };
  }

  return edgesIndex;
};

export type NodeItemState = {
  dtoType?: string;
  initiativeType?: string;
  exists: boolean;
  isActive: boolean;
};

export const selectNodeItemState =
  (id: string) =>
  (state: CanvasSlice): NodeItemState => {
    const node = indexNodes(state.nodes).byId.get(id);
    const dto = node?.data?.dto;

    return {
      dtoType: dto?.type,
      initiativeType: dto?.meta_data?.type,
      exists: Boolean(node),
      isActive: Boolean(node?.selected),
    };
  };

export type ActiveSelectionState = {
  isActiveEdgeTarget: boolean;
  isActiveEdgeSource: boolean;
  hasActiveEdge: boolean;
  hasActiveNode: boolean;
};

export const selectActiveSelection =
  (id: string) =>
  (state: CanvasSlice): ActiveSelectionState => {
    const { activeEdge } = indexEdges(state.edges);

    return {
      isActiveEdgeTarget: activeEdge?.target === id,
      isActiveEdgeSource: activeEdge?.source === id,
      hasActiveEdge: Boolean(activeEdge),
      hasActiveNode: indexNodes(state.nodes).selectedNodeId !== null,
    };
  };

export const selectIsNodeAddedToAsset =
  (id: string, selectedAssetId: string | null, isRiskManagement: boolean) =>
  (state: CanvasSlice): boolean => {
    if (!isRiskManagement) return false;

    const { byId } = indexNodes(state.nodes);

    const dto = byId.get(id)?.data?.dto as
      | { parent_asset?: string; type?: string }
      | undefined;

    if (!dto) return false;

    const currentAssetDtoId = selectedAssetId
      ? byId.get(selectedAssetId)?.data?.dto?.id
      : undefined;

    const isCurrentAssetParentForNode =
      Boolean(dto.parent_asset) && dto.parent_asset === currentAssetDtoId;

    return (
      !isCurrentAssetParentForNode &&
      Object.values(compatibleLayerTypeByLayerId).includes(
        dto.type as NodeDTOTypes,
      )
    );
  };
